import { hasLowerLevel, isMapId, mapInfo, radarToWorld, siteBox, type Level, type MapId } from "@69ly/maps";
import {
  AREAS,
  EXECUTION_KINDS,
  ExecutionInput,
  kindClock,
  kindSide,
  MAX_PLAN_OPPONENTS,
  MAX_PLAN_PLAYERS,
  planPoints,
  type Area,
  type Combo,
  type Execution,
  type ExecutionKind,
  type PlanAction,
  type PlanPlayer,
  type PlanPoint,
  type Scope,
  type Stuff,
} from "@69ly/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams, useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { BuilderRail, type BuilderCtx, type BuildMode, type Doc } from "../components/plan/BuilderRail.tsx";
import { PlanControls } from "../components/plan/PlanControls.tsx";
import { PlanStage, type StageEdit, type StageTarget } from "../components/plan/PlanStage.tsx";
import { PlanTimeline } from "../components/plan/PlanTimeline.tsx";
import { focusOn, focusOnBox } from "../components/Radar.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { PERSONAL, useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useLeave } from "../lib/leave.ts";
import {
  addAction,
  DEFAULT_ROLES,
  emptyPlan,
  endPoint,
  eventTimes,
  floorOf,
  isPlayer,
  ownerOf,
  planId,
  removeAction,
  updateAction,
  usePlanSchedule,
  usePlayhead,
  withActor,
  yawTowards,
  type PlanActor,
} from "../lib/plan.ts";
import { useMe, useTeam } from "../lib/queries.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { useCombos, useExecution, useSaveExecution } from "../lib/teamplay-queries.ts";
import { useFillHeight } from "./Executions.tsx";

/** How many steps undo remembers. */
const HISTORY = 100;

interface History {
  past: Doc[];
  present: Doc;
  future: Doc[];
}

/**
 * The plan being built, with undo and redo. Typing in one field counts as one
 * step; a drag on the map counts as one step when it ends.
 */
function useDocHistory(initial: Doc) {
  const [h, setH] = useState<History>({ past: [], present: initial, future: [] });
  const last = useRef<{ el: Element | null; at: number }>({ el: null, at: 0 });
  const dragBase = useRef<Doc | null>(null);

  const change = useCallback((fn: (d: Doc) => Doc) => {
    const el = document.activeElement;
    const typing = el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && el.type !== "checkbox" && el.type !== "radio");
    const now = Date.now();
    const merge = typing && last.current.el === el && now - last.current.at < 2000;
    last.current = { el: typing ? el : null, at: now };
    setH((s) => {
      const next = fn(s.present);
      if (next === s.present) return s;
      return merge ? { ...s, present: next, future: [] } : { past: [...s.past, s.present].slice(-HISTORY), present: next, future: [] };
    });
  }, []);
  /** A change while dragging: shown at once, remembered as one step by `endLive`. */
  const live = useCallback((fn: (d: Doc) => Doc) => {
    setH((s) => {
      if (!dragBase.current) dragBase.current = s.present;
      return { ...s, present: fn(s.present) };
    });
  }, []);
  const endLive = useCallback(() => {
    const base = dragBase.current;
    dragBase.current = null;
    if (base) setH((s) => (base === s.present ? s : { past: [...s.past, base].slice(-HISTORY), present: s.present, future: [] }));
  }, []);
  const undo = useCallback(() => setH((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] } : s)), []);
  const redo = useCallback(() => setH((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s)), []);
  return { doc: h.present, change, live, endLive, undo, redo, canUndo: h.past.length > 0, canRedo: h.future.length > 0, dirty: h.past.length > 0 };
}

/** The doc a new plan starts from: empty, or a copy of another. */
function startDoc(kind: ExecutionKind, target: Area, copy: Execution | undefined, copyTitle: (t: string) => string): Doc {
  if (copy) return { title: copyTitle(copy.title).slice(0, 60), kind: copy.kind, target: copy.target, notes: copy.notes, plan: copy.plan };
  return { title: "", kind, target, notes: "", plan: emptyPlan(kind) };
}

/** Where a new player looks: towards the target site when the map knows it. */
function lookTowardsTarget(map: MapId, target: Area, from: { x: number; y: number }): number {
  const box = target === "a" || target === "b" ? siteBox(map, target) : null;
  if (!box) return 0;
  const centre = radarToWorld(map, { u: (box.u0 + box.u1) / 2, v: (box.v0 + box.v1) / 2 });
  return Math.round(yawTowards(from, centre));
}

function Builder({
  map,
  scope,
  execution,
  initial,
  stuffs,
  combos,
}: {
  map: MapId;
  scope: Scope;
  execution?: Execution;
  initial: Doc;
  stuffs: Stuff[];
  combos: Combo[];
}) {
  const { t } = useTranslation();
  const leave = useLeave();
  const save = useSaveExecution(execution?.id);
  const history = useDocHistory(initial);
  const { doc, change, live, endLive, undo, redo } = history;
  const { plan } = doc;
  const side = kindSide(doc.kind);

  // Who can be pinned: the team's players, or only me in a personal library.
  const team = useTeam(scope === PERSONAL ? undefined : scope);
  const me = useMe().data?.user;
  const members = useMemo(
    () =>
      scope === PERSONAL
        ? me
          ? [{ id: me.id, name: me.name, avatarUrl: me.avatarUrl }]
          : []
        : (team.data?.members.map((m) => ({ id: m.userId, name: m.name, avatarUrl: m.avatarUrl })) ?? execution?.members ?? []),
    [scope, me, team.data, execution],
  );

  const { schedule, lookup } = usePlanSchedule(plan, stuffs, combos);
  const length = Math.max(schedule.span, Math.ceil(schedule.duration + 1));
  const playhead = usePlayhead(length);
  const events = useMemo(() => eventTimes(schedule, plan.chapters), [schedule, plan.chapters]);

  const [selected, setSelected] = useState<string | null>(plan.players[0]?.id ?? null);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [mode, setMode] = useState<BuildMode>(plan.players.length === 0 ? "place-player" : "move");
  const [pace, setPace] = useState<"run" | "walk" | "crouch">("run");
  const [showTargets, setShowTargets] = useState(false);
  const [tab, setTab] = useState<"players" | "opponents" | "plan">("players");
  const [level, setLevel] = useState<Level>(floorOf(map, plan.players[0]?.start.z ?? plan.bomb?.z ?? null));
  const pickZ = level === "lower" ? (mapInfo(map).lowerBelowZ ?? 0) - 100 : null;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Zoomed on what the plan uses when it opens, once: building must not move the view.
  const [view] = useState(() => {
    const points = planPoints(initial.plan, lookup);
    if (points.length) return focusOn(map, points, { maxScale: 2.2, pad: 0.14 });
    // A new retake: the site and around it, where it all happens.
    const box = initial.kind === "retake" && (initial.target === "a" || initial.target === "b") ? siteBox(map, initial.target) : null;
    return box ? focusOnBox({ u0: box.u0 - 0.15, v0: box.v0 - 0.15, u1: box.u1 + 0.15, v1: box.v1 + 0.15 }, { pad: 0, maxScale: 2.2 }) : undefined;
  });

  const actors: PlanActor[] = useMemo(() => [...plan.players, ...plan.opponents], [plan]);
  const current = actors.find((a) => a.id === selected) ?? null;

  const select = useCallback(
    (id: string | null) => {
      setSelected(id);
      setSelectedAction(null);
      if (id) setTab(plan.opponents.some((o) => o.id === id) ? "opponents" : "players");
      setMode((m) => (m === "bomb" ? m : "move"));
    },
    [plan.opponents],
  );
  const selectAction = useCallback(
    (id: string | null) => {
      setSelectedAction(id);
      if (!id) return;
      const owner = ownerOf(plan, id);
      if (owner && owner.id !== selected) {
        setSelected(owner.id);
        setTab(isPlayer(owner) ? "players" : "opponents");
      }
      const time = schedule.times.get(id);
      if (time) playhead.seek(time.start);
    },
    [plan, selected, schedule, playhead],
  );

  // Leaving with changes not saved asks first.
  const saved = useRef(false);
  useEffect(() => {
    if (!history.dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      if (saved.current) return;
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [history.dirty]);

  /** A new player (or opponent) standing at a spot, selected to be given a part. */
  const place = (p: PlanPoint, enemy: boolean) => {
    const id = planId(enemy ? "o" : "p");
    const yaw = enemy ? 0 : lookTowardsTarget(map, doc.target, p);
    change((d) => {
      if (enemy) {
        if (d.plan.opponents.length >= MAX_PLAN_OPPONENTS) return d;
        const weapon = kindSide(d.kind) === "t" ? "m4" : "ak";
        return { ...d, plan: { ...d.plan, opponents: [...d.plan.opponents, { id, label: "", weapon, start: { ...p, yaw }, actions: [] }] } };
      }
      if (d.plan.players.length >= MAX_PLAN_PLAYERS) return d;
      const slot = [1, 2, 3, 4, 5].find((n) => !d.plan.players.some((x) => x.slot === n)) ?? 1;
      const player: PlanPlayer = {
        id,
        slot,
        role: DEFAULT_ROLES[slot - 1],
        memberId: null,
        weapon: kindSide(d.kind) === "t" ? "ak" : "m4",
        start: { ...p, yaw },
        actions: [],
      };
      return { ...d, plan: { ...d.plan, players: [...d.plan.players, player].sort((a, b) => a.slot - b.slot) } };
    });
    setSelected(id);
    setSelectedAction(null);
    setTab(enemy ? "opponents" : "players");
    setMode("move");
  };

  /** A move for the selected actor: after the selected action, or at the end. */
  const addMove = (p: PlanPoint) => {
    if (!current) return;
    const after = selectedAction && (current.actions as PlanAction[]).some((x) => x.id === selectedAction) ? selectedAction : null;
    const index = after ? (current.actions as PlanAction[]).findIndex((x) => x.id === after) + 1 : undefined;
    const from = endPoint(current, lookup, index);
    if (Math.hypot(p.x - from.x, p.y - from.y) < 24) return;
    const action: PlanAction = { id: planId("a"), kind: "move", to: p, pace, at: null, after: null };
    change((d) => ({ ...d, plan: addAction(d.plan, current.id, action, after) }));
    setSelectedAction(action.id);
  };

  const edit: StageEdit = {
    onPick: (p) => {
      if (mode === "place-player") return place(p, false);
      if (mode === "place-opponent") return place(p, true);
      if (mode === "bomb") {
        change((d) => ({ ...d, plan: { ...d.plan, bomb: p } }));
        return setMode("move");
      }
      if (current) addMove(p);
      else if (plan.players.length === 0) place(p, false);
    },
    onPoint: (actor, key, p) =>
      live((d) =>
        key === "start"
          ? { ...d, plan: withActor(d.plan, actor, (a) => ({ ...a, start: { ...a.start, x: Math.round(p.x), y: Math.round(p.y) } }) as PlanActor) }
          : { ...d, plan: updateAction(d.plan, key, (x) => (x.kind === "move" ? { ...x, to: { ...x.to, x: Math.round(p.x), y: Math.round(p.y) } } : x)) },
      ),
    onAim: (actor, key, yaw) =>
      live((d) =>
        key === "start"
          ? { ...d, plan: withActor(d.plan, actor, (a) => ({ ...a, start: { ...a.start, yaw: Math.round(yaw) } }) as PlanActor) }
          : { ...d, plan: updateAction(d.plan, key, (x) => (x.kind === "hold" ? { ...x, yaw: Math.round(yaw) } : x)) },
      ),
    onDone: endLive,
  };

  // Singles and combos offered on the map: where each is thrown from, for the selected player.
  const targets: StageTarget[] = useMemo(() => {
    // Only while adding to a player: placing someone or the bomb clicks the map itself.
    if (!showTargets || mode !== "move" || !current || !isPlayer(current)) return [];
    const out: StageTarget[] = [];
    for (const s of stuffs)
      if (s.side === side) out.push({ key: `s${s.id}`, kind: "throw", id: s.id, title: s.title, grenade: s.grenade, x: s.throw.x, y: s.throw.y, z: s.throw.z });
    for (const c of combos) {
      const first = c.side === side ? c.steps[0]?.stuff : undefined;
      if (first) out.push({ key: `c${c.id}`, kind: "combo", id: c.id, title: c.title, grenade: first.grenade, x: first.throw.x, y: first.throw.y, z: first.throw.z });
    }
    return out;
  }, [showTargets, mode, current, stuffs, combos, side]);
  const onTarget = (x: StageTarget) => {
    if (!current) return;
    const after = selectedAction && (current.actions as PlanAction[]).some((a) => a.id === selectedAction) ? selectedAction : null;
    const action: PlanAction =
      x.kind === "throw"
        ? { id: planId("a"), kind: "throw", stuffId: x.id, lineup: null, at: null, after: null }
        : { id: planId("a"), kind: "combo", comboId: x.id, lineup: null, gap: 1.2, at: null, after: null };
    change((d) => ({ ...d, plan: addAction(d.plan, current.id, action, after) }));
    setSelectedAction(action.id);
  };

  // Keys: undo, redo, delete the selected action, 1–5 pick a player, Escape steps back.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest("input, textarea, select, [contenteditable]")) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (mod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedAction) {
        e.preventDefault();
        change((d) => ({ ...d, plan: removeAction(d.plan, selectedAction) }));
        setSelectedAction(null);
      } else if (!mod && !e.altKey && /^[1-5]$/.test(e.key)) {
        const p = plan.players.find((x) => x.slot === Number(e.key));
        if (p) select(p.id);
      } else if (e.key === "Escape") {
        if (mode !== "move") setMode("move");
        else if (selectedAction) setSelectedAction(null);
        else if (selected) setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, change, selectedAction, selected, plan.players, select, mode]);

  const setKind = (k: ExecutionKind) => {
    if (k === doc.kind) return;
    const throws = actors.some((a) => (a.actions as PlanAction[]).some((x) => x.kind === "throw" || x.kind === "combo"));
    const sideChanges = kindSide(k) !== side;
    if (sideChanges && throws && !window.confirm(t("plan.kindChangeDropsThrows"))) return;
    change((d) => {
      let next = d.plan;
      if (sideChanges) {
        const strip = <A extends PlanActor>(a: A): A => ({ ...a, actions: (a.actions as PlanAction[]).filter((x) => x.kind !== "throw" && x.kind !== "combo") });
        next = { ...next, players: next.players.map(strip), opponents: next.opponents };
      }
      // Another clock: the window starts over when it does not fit.
      if (next.window.from > kindClock(k) || (d.kind === "retake") !== (k === "retake")) next = { ...next, window: emptyPlan(k).window, chapters: [] };
      return { ...d, kind: k, plan: next };
    });
  };

  const ctx: BuilderCtx = {
    map,
    plan,
    schedule,
    lookup,
    stuffs,
    combos,
    members,
    doc,
    people: members,
    changeDoc: change,
    selected,
    select,
    selectedAction,
    selectAction,
    mode,
    setMode,
    pace,
    setPace,
    showTargets,
    setShowTargets,
    elapsed: playhead.elapsed,
  };

  const page = useRef<HTMLDivElement>(null);
  const height = useFillHeight(page);
  const names = {
    short: (a: PlanActor) => (isPlayer(a) ? (members.find((m) => m.id === a.memberId)?.name ?? t(`role.${a.role}`)) : a.label || t("plan.opponent")),
    title: (a: PlanActor) => (isPlayer(a) ? `${a.slot} · ${t(`role.${a.role}`)} · ${t(`plan.weapon.${a.weapon}`)}` : [t("plan.opponent"), a.label].filter(Boolean).join(" · ")),
  };

  const submit = async () => {
    setError(null);
    if (!doc.title.trim()) return setError(t("plan.titleNeeded"));
    const input = ExecutionInput.safeParse({ map, kind: doc.kind, target: doc.target, title: doc.title.trim(), notes: doc.notes, plan: doc.plan });
    if (!input.success) {
      const issue = input.error.issues[0];
      return setError(issue ? `${issue.path.join(" › ")}: ${issue.message}` : t("errors.invalid_input"));
    }
    setBusy(true);
    try {
      const out = await save.mutateAsync(execution ? input.data : { ...input.data, scope });
      saved.current = true;
      if (execution) leave.back(`/library/${map}/executions?e=${out.id}`);
      else leave.to(`/library/${map}/executions?e=${out.id}`);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  const hint =
    mode === "place-player"
      ? t(plan.players.length === 0 ? "plan.hintFirst" : "plan.hintPlacePlayer")
      : mode === "place-opponent"
        ? t("plan.hintPlaceOpponent")
        : mode === "bomb"
          ? t("plan.hintBomb")
          : current
            ? t("plan.hintMove", { name: names.title(current), pace: t(`plan.paces.${pace}`) })
            : plan.players.length === 0
              ? t("plan.hintFirst")
              : t("plan.hintSelect");

  return (
    <div className="library plan-builder">
      <div className="plan-builder-head card">
        <input
          className="plan-title-input"
          value={doc.title}
          maxLength={60}
          placeholder={t("plan.titlePlaceholder")}
          aria-label={t("plan.title")}
          onChange={(e) => change((d) => ({ ...d, title: e.target.value }))}
        />
        <div className="segmented segmented-small" role="group" aria-label={t("plan.kindLabel")}>
          {EXECUTION_KINDS.map((k) => (
            <button key={k} type="button" className="seg" aria-pressed={doc.kind === k} onClick={() => setKind(k)} title={t(`plan.kindHelp.${k}`)}>
              {t(`plan.kind.${k}`)}
            </button>
          ))}
        </div>
        <label className="field-inline small">
          {t("plan.target")}
          <select value={doc.target} onChange={(e) => change((d) => ({ ...d, target: e.target.value as Area }))}>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {t(`area.${a}`)}
              </option>
            ))}
          </select>
        </label>
        <span className={`side-tag side-${side}`}>{t(`side.${side}`)}</span>
        <div className="row plan-builder-actions">
          <button type="button" className="btn btn-quiet btn-icon" onClick={undo} disabled={!history.canUndo} aria-label={t("plan.undo")} title={`${t("plan.undo")} (Ctrl+Z)`}>
            <Icon name="undo" size={15} />
          </button>
          <button type="button" className="btn btn-quiet btn-icon" onClick={redo} disabled={!history.canRedo} aria-label={t("plan.redo")} title={`${t("plan.redo")} (Ctrl+Y)`}>
            <Icon name="redo" size={15} />
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => leave.back(`/library/${map}/executions${execution ? `?e=${execution.id}` : ""}`)}>
            {t("form.cancel")}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
            {busy ? t("form.saving") : t("plan.save")}
          </button>
        </div>
      </div>
      {error && <Banner tone="warning">{error}</Banner>}
      <p className="plan-phone-note small muted">{t("plan.buildOnDesktop")}</p>
      <div className="plan-page is-building" ref={page} style={height ? { height } : undefined}>
        <div className="plan-main">
          <div className="plan-map-wrap">
            <div className="plan-map map-frame">
              <PlanStage
                map={map}
                mapName={mapInfo(map).name}
                level={level}
                plan={plan}
                schedule={schedule}
                elapsed={playhead.elapsed}
                focus={null}
                selected={selected}
                view={view}
                actorTitle={names.title}
                edit={edit}
                targets={targets}
                onTarget={onTarget}
                onSelectActor={select}
                pickZ={pickZ}
              />
              <div className="map-overlay map-overlay-top glass plan-hint" role="status">
                <Icon name={mode === "move" ? "route" : "plus"} size={13} />
                <span>{hint}</span>
                {mode !== "move" && (
                  <button type="button" className="link-button" onClick={() => setMode("move")}>
                    {t("form.cancel")}
                  </button>
                )}
              </div>
              {hasLowerLevel(map) && (
                <div className="map-overlay map-overlay-bottom glass">
                  <div className="segmented" role="group" aria-label={t("level.label")}>
                    {(["upper", "lower"] as const).map((l) => (
                      <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                        {t(`level.${l}`)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <aside className="plan-rail card">
            <BuilderRail ctx={ctx} tab={tab} setTab={setTab} />
          </aside>
        </div>
        <div className="plan-bottom card">
          <PlanControls plan={plan} schedule={schedule} length={length} events={events} playhead={playhead} />
          <PlanTimeline
            plan={plan}
            schedule={schedule}
            length={length}
            elapsed={playhead.elapsed}
            onSeek={playhead.seek}
            focus={null}
            selectedAction={selectedAction}
            onSelectAction={(_, id) => selectAction(id)}
            actorName={names.short}
          />
        </div>
      </div>
    </div>
  );
}

/** The library the builder picks from, with what the plan already uses (in case it moved). */
function usePlanLibrary(scope: Scope, map: string, execution?: Execution) {
  const singles = useStuffs(scope, map);
  const combos = useCombos(scope, map);
  const merged = useMemo(() => {
    if (!singles.data || !combos.data) return null;
    const s = [...singles.data];
    for (const x of execution?.stuffs ?? []) if (!s.some((y) => y.id === x.id)) s.push(x);
    const c = [...combos.data];
    for (const x of execution?.combos ?? []) if (!c.some((y) => y.id === x.id)) c.push(x);
    return { stuffs: s, combos: c };
  }, [singles.data, combos.data, execution]);
  return { merged, error: singles.error ?? combos.error, retry: () => void Promise.all([singles.refetch(), combos.refetch()]) };
}

export function NewExecutionPage() {
  const { t } = useTranslation();
  const { map } = useParams();
  const [params] = useSearchParams();
  const { scope } = useCurrentTeam();
  const copyId = params.get("copy") ?? undefined;
  const copy = useExecution(copyId);
  const lib = usePlanLibrary(scope, isMapId(map) ? map : "", copy.data);
  if (!isMapId(map)) return <Navigate to="/library" replace />;
  if (copyId && copy.isPending) return <Loading />;
  if (copyId && copy.error) return <ErrorState message={errorText(t, copy.error)} onRetry={() => void copy.refetch()} />;
  if (lib.error) return <ErrorState message={errorText(t, lib.error)} onRetry={lib.retry} />;
  if (!lib.merged) return <Loading />;
  const kindParam = params.get("kind");
  const kind: ExecutionKind = (EXECUTION_KINDS as readonly string[]).includes(kindParam ?? "") ? (kindParam as ExecutionKind) : "execute";
  const targetParam = params.get("target");
  const target: Area = (AREAS as readonly string[]).includes(targetParam ?? "") ? (targetParam as Area) : "a";
  const initial = startDoc(kind, target, copy.data, (title) => t("plan.copyOf", { title }));
  return <Builder key={copyId ?? kind} map={map} scope={scope} initial={initial} stuffs={lib.merged.stuffs} combos={lib.merged.combos} />;
}

export function EditExecutionPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const execution = useExecution(id);
  const scope: Scope = execution.data ? (execution.data.teamId ?? PERSONAL) : PERSONAL;
  const lib = usePlanLibrary(scope, execution.data?.map ?? "", execution.data);
  if (execution.isPending) return <Loading />;
  if (execution.error) return <ErrorState message={errorText(t, execution.error)} onRetry={() => void execution.refetch()} />;
  const e = execution.data;
  if (!isMapId(e.map) || !e.canEdit) return <Navigate to={`/library/${e.map}/executions?e=${e.id}`} replace />;
  if (lib.error) return <ErrorState message={errorText(t, lib.error)} onRetry={lib.retry} />;
  if (!lib.merged) return <Loading />;
  return (
    <Builder
      key={e.id}
      map={e.map}
      scope={scope}
      execution={e}
      initial={{ title: e.title, kind: e.kind, target: e.target, notes: e.notes, plan: e.plan }}
      stuffs={lib.merged.stuffs}
      combos={lib.merged.combos}
    />
  );
}
